import type { KitchenTimer } from "./models";

export const TIMER_PRESETS_MIN = [1, 3, 5, 10, 15, 30] as const;
export const MAX_TIMER_MS = 24 * 3_600_000;
export const MAX_ACTIVE_TIMERS = 12;
/** A finished timer stops sounding after this long, even if nobody dismisses it. */
export const ALARM_MAX_MS = 2 * 60_000;

export type TimerPhase = "running" | "paused" | "done";

export function timerRemainingMs(t: Pick<KitchenTimer, "endAt" | "remainingMs">, nowMs: number): number {
  if (t.endAt === null) return Math.max(0, t.remainingMs ?? 0);
  return Math.max(0, Date.parse(t.endAt) - nowMs);
}

export function timerPhase(t: Pick<KitchenTimer, "endAt" | "remainingMs">, nowMs: number): TimerPhase {
  if (t.endAt === null) return "paused";
  return Date.parse(t.endAt) <= nowMs ? "done" : "running";
}

/** True while a finished timer should still be sounding (first 2 minutes after it ended). */
export function timerShouldRing(t: Pick<KitchenTimer, "endAt" | "remainingMs">, nowMs: number): boolean {
  if (t.endAt === null) return false;
  const end = Date.parse(t.endAt);
  return end <= nowMs && nowMs - end < ALARM_MAX_MS;
}

/** "4:05", "12:00", "1:02:03". Rounds up so a timer never shows 0:00 while still running. */
export function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const ss = String(s).padStart(2, "0");
  return h ? `${h}:${String(m).padStart(2, "0")}:${ss}` : `${m}:${ss}`;
}

/** Short label for a duration: "5 min", "1 min 30 sec", "1 hr 30 min", "45 sec". */
export function formatDuration(ms: number): string {
  const totalSec = Math.round(ms / 1000);
  if (totalSec < 60) return `${totalSec} sec`;
  if (totalSec < 600 && totalSec % 60) return `${Math.floor(totalSec / 60)} min ${totalSec % 60} sec`;
  const totalMin = Math.round(totalSec / 60);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (!h) return `${m} min`;
  return m ? `${h} hr ${m} min` : `${h} hr`;
}

/**
 * Microwave-style entry: digits fill from the right as h:mm:ss, so typing
 * 1, 3, 0 means 1:30. Minutes/seconds over 59 are allowed ("90" = 90 s),
 * like a microwave. Returns whole seconds.
 */
export function microwaveToSec(digits: string): number {
  const d = digits.replace(/\D/g, "").slice(-6).padStart(6, "0");
  const h = Number(d.slice(0, 2));
  const m = Number(d.slice(2, 4));
  const s = Number(d.slice(4, 6));
  return h * 3600 + m * 60 + s;
}

/** Display for microwave entry: "0:00", "1:30", "1:05:00". */
export function microwaveText(digits: string): string {
  const d = digits.replace(/\D/g, "").slice(-6).padStart(6, "0");
  const h = Number(d.slice(0, 2));
  return h ? `${h}:${d.slice(2, 4)}:${d.slice(4, 6)}` : `${Number(d.slice(2, 4))}:${d.slice(4, 6)}`;
}
